import { Fragment, type ReactNode } from 'react';
import { ExternalLink } from 'lucide-react';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { pbFileUrl } from '@/utils/pb-file-url';
import type { Approacher } from '../types';
import { ProfilePills } from './profile-pills';

function buildHandles(a: Approacher): string[] {
  const list: string[] = [];
  if (a.ig_username) list.push(a.ig_username);
  if (a.personal_ig_username && a.personal_ig_username !== a.ig_username) {
    list.push(a.personal_ig_username);
  }
  return list;
}

function HandleLine({ a }: { a: Approacher }): ReactNode {
  const handles = buildHandles(a);
  if (!handles.length) return null;
  return (
    <div className="inline-flex flex-wrap items-center gap-1 text-xs text-text-tertiary">
      {handles.map((ig, i) => (
        <Fragment key={ig}>
          {i > 0 && <span>-</span>}
          <a
            href={`https://instagram.com/${ig}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-text-primary"
          >
            @{ig}
          </a>
        </Fragment>
      ))}
      <ExternalLink size={11} />
    </div>
  );
}

function NameAndPills({
  a,
  onStatusEdit,
}: {
  a: Approacher;
  onStatusEdit: () => void;
}): ReactNode {
  const display =
    [a.first_name, a.last_name].filter(Boolean).join(' ') || a.ig_username;
  return (
    <div className="flex flex-wrap items-center gap-x-md gap-y-xs">
      <div className="flex items-center gap-sm">
        {!!a.player_number && (
          <Badge variant="default" className="font-mono">
            #{a.player_number}
          </Badge>
        )}
        <p className="truncate text-base font-semibold text-text-primary">
          {display}
        </p>
      </div>
      <ProfilePills a={a} onStatusEdit={onStatusEdit} />
    </div>
  );
}

export function ApproacherProfile({
  a,
  onStatusEdit,
}: {
  a: Approacher;
  onStatusEdit: () => void;
}): ReactNode {
  const src = a.photo ? pbFileUrl(a, a.photo) : undefined;
  return (
    <div className="flex items-start gap-md">
      <Avatar src={src} name={a.ig_username} size="lg" />
      <div className="min-w-0 flex-1 space-y-xs">
        <NameAndPills a={a} onStatusEdit={onStatusEdit} />
        <HandleLine a={a} />
      </div>
    </div>
  );
}
